import { configureStore } from '@reduxjs/toolkit';
import transactionsReducer from './slices/transactionsSlice';
import statusReducer, { statusInitialState } from './slices/statusSlice';
import portfoliosReducer from './slices/portfoliosSlice';
import companiesReducer from './slices/companiesSlice';
import stocksReducer from './slices/stocksSlice';
import tagsReducer from './slices/tagsSlice';
import watchedStocksReducer from './slices/watchedStocksSlice';
import boutiquesReducer from './slices/boutiquesSlice';
import type { StatusState } from './types';

const AUTH_STORAGE_KEY = 'caramel-auth-v1';

function loadPersistedAuth(): Partial<Pick<StatusState, 'token' | 'userID' | 'firebaseToken'>> | null {
    try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        return {
            token: typeof parsed.token === 'string' ? parsed.token : '',
            userID: typeof parsed.userID === 'number' ? parsed.userID : -1,
            firebaseToken: typeof parsed.firebaseToken === 'string' ? parsed.firebaseToken : '',
        };
    } catch {
        return null;
    }
}

function savePersistedAuth(auth: Pick<StatusState, 'token' | 'userID' | 'firebaseToken'>): void {
    try {
        if (!auth.token && !auth.firebaseToken) {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            return;
        }
        localStorage.setItem(
            AUTH_STORAGE_KEY,
            JSON.stringify({
                token: auth.token,
                userID: auth.userID,
                firebaseToken: auth.firebaseToken,
            })
        );
    } catch {
        /* ignore quota / private mode */
    }
}

export function clearPersistedAuth(): void {
    try {
        localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

const persistedAuth = loadPersistedAuth();

const hasPersistedSession =
    persistedAuth &&
    ((persistedAuth.token && persistedAuth.token.length > 0) ||
        (persistedAuth.firebaseToken && persistedAuth.firebaseToken.length > 0));

const preloadedState = hasPersistedSession
    ? {
          status: {
              ...statusInitialState,
              ...persistedAuth,
              showLoginDialog: false,
          } as StatusState,
      }
    : undefined;

export const store = configureStore({
    reducer: {
        transactions: transactionsReducer,
        status: statusReducer,
        portfolios: portfoliosReducer,
        companies: companiesReducer,
        stocks: stocksReducer,
        tags: tagsReducer,
        watchedStocks: watchedStocksReducer,
        boutiques: boutiquesReducer,
    },
    preloadedState,
});

let lastPersisted: Pick<StatusState, 'token' | 'userID' | 'firebaseToken'> | null = null;
store.subscribe(() => {
    const { token, userID, firebaseToken } = store.getState().status;
    if (
        lastPersisted &&
        lastPersisted.token === token &&
        lastPersisted.userID === userID &&
        lastPersisted.firebaseToken === firebaseToken
    ) {
        return;
    }
    lastPersisted = { token, userID, firebaseToken };
    savePersistedAuth({ token, userID, firebaseToken });
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
